import {useForm, type UseFormProps} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
// import {setCategoryObject} from '@netx/core-category/api/setCategoryObject';
import {getCategoryCreateError} from '../errors';

import {z} from 'zod';
import {ICategory} from '../schema';
import {currentUserIsAdmin} from '@netx/core-permissions-next/enablers';
import {useMemo} from 'react';

const CategoryCreateFormValuesSchema = z.object({
	name: z.string().min(1, i18n.t('i18n.required')),
	description: z.string(),
	parentid: z.number().int().gt(0, i18n.t('i18n.required')),
});

export type ICategoryCreateFormValues = z.infer<
	typeof CategoryCreateFormValuesSchema
>;

/** Default values for the form */
export const defaultCategoryCreateFormValues: ICategoryCreateFormValues = {
	name: '',
	description: '',
	parentid: 1,
};

export interface UseCategoryCreateFormParams extends UseFormProps<ICategoryCreateFormValues> {
	initialValues?: Partial<ICategoryCreateFormValues>;
	submit: (data: ICategoryCreateFormValues) => Promise<ICategory>;
}

/**
 * react-hook-form for merging vocabulary values
 */
export const useCategoryCreateForm = ({
	initialValues = defaultCategoryCreateFormValues,
	submit,
	...props
}: UseCategoryCreateFormParams) => {
	const isAdmin = currentUserIsAdmin();

	const defaultValues = useMemo(() => {
		return isAdmin
			? initialValues
			: {
					...initialValues,
					parentid: initialValues.parentid === 1 ? 0 : initialValues.parentid,
				};
	}, [initialValues, isAdmin]);

	const form = useForm<ICategoryCreateFormValues>({
		mode: 'all',
		criteriaMode: 'all',
		defaultValues,
		resolver: zodResolver(CategoryCreateFormValuesSchema),
		...props,
	});

	/** Reset the form to the default values */
	const reset = useConstCallback(
		(values?: Partial<ICategoryCreateFormValues>) =>
			form.reset({
				...defaultCategoryCreateFormValues,
				...values,
				parentid:
					(values?.parentid || initialValues.parentid) === 1
						? 0
						: values?.parentid || initialValues.parentid,
			}),
	);

	/** Form submission handler */
	const onSubmit = form.handleSubmit(async (data) => {
		return submit(data)
			.then((response) => {
				reset();
				return response;
			})
			.catch((error) => {
				form.setError('root', {
					message: getCategoryCreateError('create', error.code),
					type: 'error',
				});
			});
	});

	return {
		form,
		onSubmit,
		reset,
	};
};
